import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Zap,
  RefreshCw,
  ChevronRight,
  User,
  AlertCircle,
  Bell,
} from 'lucide-react';
import { fetchOutletSlots, flattenSlots, formatDateLabel, formatTime } from '../services/slotsService';

/* ─── Sub-components ─── */

function SlotCard({ slot, onSelect }) {
  return (
    <button
      className="sp-slot-card press-scale-card"
      onClick={() => onSelect(slot)}
      style={{ '--card-accent': slot.specialty.accent }}
    >
      <div className="sp-slot-top">
        <div className="sp-slot-specialty-tag" style={{ background: slot.specialty.color, color: slot.specialty.accent }}>
          <span>{slot.specialty.emoji}</span>
          <span>{slot.specialty.name}</span>
        </div>
        <div className="sp-slot-time-main">
          <Clock size={14} color={slot.specialty.accent} />
          <span className="sp-slot-time-text">{formatTime(slot.init)}</span>
        </div>
      </div>

      <div className="sp-slot-range">
        {formatTime(slot.init)} → {formatTime(slot.end)}
      </div>

      {/* Doctor info */}
      <div className="sp-slot-doctors">
        <div className="sp-slot-doctor">
          {slot.professional.photo ? (
            <img src={slot.professional.photo} alt={slot.professional.name} className="sp-slot-doctor-photo" />
          ) : (
            <div className="sp-slot-doctor-photo sp-slot-doctor-photo--placeholder">
              <User size={12} />
            </div>
          )}
          <span>{slot.professional.name}</span>
        </div>
      </div>

      <div className="sp-slot-cta">
        <span>Seleccionar</span>
        <ChevronRight size={16} />
      </div>
    </button>
  );
}

function SkeletonSlot() {
  return (
    <div className="sp-slot-card sp-slot-card--skeleton">
      <div className="skeleton-line" style={{ width: '50%', height: 16 }} />
      <div className="skeleton-line" style={{ width: '70%', height: 12, marginTop: 8 }} />
      <div className="skeleton-line" style={{ width: '60%', height: 12, marginTop: 8 }} />
    </div>
  );
}

/* Chip discreto: un horario locked que se abrirá si nadie lo agenda antes.
   No es clickeable; solo transmite que viene actividad sin competir con las
   cards agendables. El detalle (especialidad/doctora) va en el title. */
function UpcomingChip({ slot }) {
  return (
    <span
      className="sp-time-chip"
      style={{ background: slot.specialty.color, color: slot.specialty.accent }}
      title={`${slot.specialty.name} · ${slot.professional.name} · se abre ~${formatTime(slot.init)}`}
    >
      <span>{slot.specialty.emoji}</span>
      <span>{formatTime(slot.init)}</span>
    </span>
  );
}

/* ═══════════════════════════════════════════ */
/* ─── Main SlotPicker Component ─── */
/* ═══════════════════════════════════════════ */

export default function SlotPicker({ onBack, onSelectSlot, onJoinWaitlist }) {
  const [slots, setSlots] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadSlots = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const { slots: apiSlots, upcoming: apiUpcoming } = await fetchOutletSlots();
      setSlots(flattenSlots(apiSlots, true));
      setUpcoming(flattenSlots(apiUpcoming, false));
    } catch (err) {
      console.error('Error loading slots:', err);
      setSlots([]);
      setUpcoming([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const handleSelectSlot = (slot) => {
    onSelectSlot({
      slotId: slot.id,
      specialty: slot.specialty,
      time: slot.init,
      timeEnd: slot.end,
      date: slot.date,
      professional: slot.professional,
    });
  };

  // "Se abren pronto" agrupados por fecha (Hoy / Mañana / weekday)
  const upcomingByDate = {};
  upcoming.forEach(slot => {
    if (!upcomingByDate[slot.date]) upcomingByDate[slot.date] = [];
    upcomingByDate[slot.date].push(slot);
  });
  const upcomingDateKeys = Object.keys(upcomingByDate).sort();

  const hasAny = slots.length > 0 || upcoming.length > 0;

  return (
    <div className="page-container" style={{ paddingBottom: 40, maxWidth: 600 }}>
      <button className="back-button animate-fadeIn" onClick={onBack} id="back-to-landing">
        <ArrowLeft size={18} />
        Volver
      </button>

      {/* Header */}
      <div className="animate-fadeInUp" style={{ marginTop: 12, marginBottom: 24 }}>
        <div className="sp-header-badge">
          <Zap size={14} />
          <span>Consultas de Valoración</span>
          <span className="available-pulse" />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.5px', marginTop: 10 }}>
          Horarios de esta semana
        </h1>
        <p style={{ fontSize: 14, color: 'var(--brand-deep)', opacity: 0.7, marginTop: 4, lineHeight: 1.5 }}>
          Agenda las que están libres ahora, o mira las que se abren pronto. Son consultas gratuitas con doctoras certificadas.
        </p>
      </div>

      {/* Refresh row */}
      {!loading && hasAny && (
        <div className="sp-refresh-row animate-fadeIn">
          <div className="sp-count-group">
            {slots.length > 0 && (
              <span className="sp-total-badge">{slots.length} gratis ahora</span>
            )}
            {upcoming.length > 0 && (
              <span className="sp-total-badge sp-total-badge--soft">{upcoming.length} esta semana</span>
            )}
          </div>
          <button
            className="available-refresh press-scale"
            onClick={() => loadSlots(true)}
            disabled={refreshing}
          >
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            <span>Actualizar</span>
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="sp-slots-list">
          {[1, 2, 3].map(i => <SkeletonSlot key={i} />)}
        </div>
      )}

      {/* Disponibles ahora (agendables, gratis) */}
      {!loading && slots.length > 0 && (
        <div className="sp-section animate-fadeInUp-1">
          <div className="sp-section-head">
            <h2 className="sp-section-title">Disponibles ahora</h2>
            <p className="sp-section-sub">Gratis y listas para agendar.</p>
          </div>
          <div className="sp-slots-list">
            {slots.map(slot => (
              <SlotCard
                key={`now-${slot.id}-${slot.professional.id}`}
                slot={slot}
                onSelect={handleSelectSlot}
              />
            ))}
          </div>
        </div>
      )}

      {/* Nada libre ahora mismo, pero sí hay próximas: invita a formarse */}
      {!loading && slots.length === 0 && upcoming.length > 0 && (
        <div className="sp-section animate-fadeInUp-1">
          <div className="sp-section-head">
            <h2 className="sp-section-title">Disponibles ahora</h2>
            <p className="sp-section-sub">Ninguna libre en este momento.</p>
          </div>
          <div className="sp-waitlist-cta">
            <div className="sp-waitlist-cta-icon">
              <Bell size={18} />
            </div>
            <div className="sp-waitlist-cta-text">
              <h3>Nada libre ahora mismo</h3>
              <p>Se liberan a lo largo del día. Fórmate en la fila y te avisamos por WhatsApp en cuanto haya una para ti.</p>
            </div>
            {onJoinWaitlist && (
              <button className="btn-primary press-scale" onClick={onJoinWaitlist}>
                Quiero formarme
                <ArrowRight size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Más esta semana (locked, informativo, compacto) */}
      {!loading && upcoming.length > 0 && (
        <div className="sp-section animate-fadeInUp-2">
          <div className="sp-section-head">
            <h2 className="sp-section-title">Más esta semana</h2>
            <p className="sp-section-sub">Se abren si nadie las toma antes. Pueden ser gratis para ti.</p>
          </div>
          {upcomingDateKeys.map(date => (
            <div key={date} className="sp-upcoming-day">
              <div className="sp-date-label">{formatDateLabel(date)}</div>
              <div className="sp-chip-row">
                {upcomingByDate[date].map(slot => (
                  <UpcomingChip key={`up-${slot.id}-${slot.professional.id}`} slot={slot} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Fila virtual: disponible aunque haya horarios, para recibir aviso */}
      {!loading && slots.length > 0 && onJoinWaitlist && (
        <div className="sp-waitlist-cta animate-fadeInUp-2">
          <div className="sp-waitlist-cta-icon">
            <Bell size={18} />
          </div>
          <div className="sp-waitlist-cta-text">
            <h3>¿No ves tu horario ideal?</h3>
            <p>Fórmate en la fila y te avisamos por WhatsApp en cuanto se libere una consulta para ti.</p>
          </div>
          <button className="btn-primary press-scale" onClick={onJoinWaitlist}>
            Quiero formarme
            <ArrowRight size={18} />
          </button>
        </div>
      )}

      {/* Empty real: nada en toda la semana */}
      {!loading && !hasAny && (
        <div className="sp-empty animate-fadeInUp-1">
          <AlertCircle size={36} color="var(--brand-deep)" />
          <h3>Por ahora no hay horarios esta semana</h3>
          <p>Las consultas se liberan a lo largo del día. Fórmate en la fila y te avisamos en cuanto haya una para ti.</p>
          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            {onJoinWaitlist && (
              <button className="btn-primary press-scale" onClick={onJoinWaitlist}>
                Fórmate en la fila
              </button>
            )}
            <button className="btn-secondary press-scale" onClick={() => loadSlots(true)}>
              <RefreshCw size={16} />
              Reintentar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
